import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';

export default function ConsentScreen({ onAgree }) {
  const navigate = useNavigate();
  const [privacy, setPrivacy] = useState(false);
  const [terms, setTerms] = useState(false);
  const [age, setAge] = useState(false);
  const allChecked = privacy && terms && age;
  const checkedCount = [privacy, terms, age].filter(Boolean).length;

  const handleAgree = () => {
    if (!allChecked) return;
    localStorage.setItem('consent_agreed', 'true');
    onAgree?.();
    navigate('/', { replace: true });
  };

  return (
    <main style={S.page}>
      <style>{CSS}</style>
      <div style={S.inner}>
        <header style={S.header}>
          <span style={S.wordmark}>jtalk<span style={S.dot}>.</span></span>
          <span style={S.headerNote}>日本語で、世界とつながる。</span>
        </header>

        <div style={S.intro}>
          <p style={S.eyebrow}>はじめる前に</p>
          <h1 style={S.title}>気持ちよく話せる、<br />場所にしよう。</h1>
          <p style={S.lead}>あなたの情報と、ここでのやりとりについて。<br />ご利用前に、大切なことをご確認ください。</p>
        </div>

        <section style={S.policy} aria-labelledby="consent-privacy-title">
          <div style={S.sectionHeading}>
            <span style={S.number}>01</span>
            <h2 id="consent-privacy-title" style={S.sectionTitle}>あなたの情報について</h2>
          </div>
          <p style={S.body}>JTALK（運営：SE）は、アカウント情報・学習データ・利用状況・決済情報（Apple / Stripe 経由）等を、サービス提供・不正防止・サポートのために利用します。</p>
          <p style={{ ...S.body, marginTop: 10 }}>AI 機能では Anthropic（Claude）へコンテンツが送信される場合があります。個人情報の販売は行いません。</p>
          <details className="ct-details" style={S.details}>
            <summary>データの削除・お問い合わせ</summary>
            <p style={{ ...S.body, marginTop: 10 }}>アカウント削除後、原則30日以内にデータを削除します。ポリシー全文は日本語・英語・フランス語・ヒンディー語でご確認いただけます。</p>
            <p style={{ ...S.body, marginTop: 8 }}>お問い合わせ：<a style={S.link} href="mailto:jtalk.se1@gmail.com">jtalk.se1@gmail.com</a><br />最終更新日：2026年6月8日</p>
          </details>
          <a data-control="row" data-control-id="ConsentScreen-documentLink" className="ct-link" href="/privacy" style={S.documentLink}>プライバシーポリシーを読む <Arrow /></a>
        </section>

        {/* 登録・ログイン前に不寛容ポリシーを明示する。 */}
        <section style={S.safety} aria-labelledby="consent-safety-title">
          <div style={S.sectionHeading}>
            <span style={S.number}>02</span>
            <h2 id="consent-safety-title" style={S.sectionTitle}>相手を大切にすること</h2>
          </div>
          <p style={S.body}><strong style={S.strong}>嫌がらせ・不適切な投稿は一切許容しません。</strong>通報は24時間以内に確認し、違反したコンテンツは削除、投稿者は利用停止となります。</p>
          <p style={S.safetyNote}>投稿の「⋯」から通報できます。ブロックは相手のプロフィールから。</p>
          <Link data-control="row" data-control-id="ConsentScreen-documentLink" className="ct-link" to="/terms" style={S.documentLink}>利用規約（EULA）を読む <Arrow /></Link>
        </section>

        <section style={S.confirmation} aria-labelledby="consent-confirm-title">
          <div style={S.confirmHeading}>
            <h2 id="consent-confirm-title" style={S.sectionTitle}>確認と同意</h2>
            <span style={S.counter} aria-live="polite">{checkedCount} / 3</span>
          </div>
          <div style={S.checkList}>
            <ConsentCheck checked={privacy} onChange={setPrivacy}>
              <a href="/privacy" style={S.link}>プライバシーポリシー</a><br /><span style={S.checkSub}>を読み、同意します</span>
            </ConsentCheck>
            <ConsentCheck checked={terms} onChange={setTerms}>
              <Link to="/terms" style={S.link}>利用規約（EULA）</Link><br /><span style={S.checkSub}>を読み、同意します</span>
            </ConsentCheck>
            <ConsentCheck checked={age} onChange={setAge}>13歳以上であることを確認します</ConsentCheck>
          </div>
          <button data-control="primary" data-control-id="ConsentScreen-submitBtn" className="ct-submit" type="button" style={{ ...S.submitBtn, ...(!allChecked ? S.submitBtnDisabled : {}) }} onClick={handleAgree} disabled={!allChecked}>
            同意してはじめる
          </button>
          <p style={S.footer} aria-live="polite">{allChecked ? '準備ができました。JTALKへようこそ。' : '3つの項目をご確認いただくと、先へ進めます。'}</p>
        </section>
      </div>
    </main>
  );
}

function Arrow() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14m-5-5 5 5-5 5" /></svg>;
}

function ConsentCheck({ checked, onChange, children }) {
  return (
    <label className="ct-check" style={{ ...S.checkLabel, ...(checked ? { background: 'var(--primary-ultra)' } : {}) }}>
      <input className="ct-checkbox" type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} style={S.checkbox} />
      <span>{children}</span>
    </label>
  );
}

const S = {
  page: { minHeight: '100vh', background: 'var(--surface)', padding: 'calc(20px + env(safe-area-inset-top)) 24px calc(28px + env(safe-area-inset-bottom))', display: 'flex', justifyContent: 'center' },
  inner: { width: '100%', maxWidth: 480 },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 },
  wordmark: { fontSize: 32, fontWeight: 900, fontStyle: 'italic', letterSpacing: '-0.07em', lineHeight: 1, color: 'var(--text)' },
  dot: { color: 'var(--primary)' },
  headerNote: { fontSize: 10, fontWeight: 700, color: 'var(--text-secondary)' },
  intro: { padding: '36px 0 30px' },
  eyebrow: { fontSize: 11, fontWeight: 800, color: 'var(--primary-dark)', letterSpacing: '0.12em', marginBottom: 12 },
  title: { fontSize: 'clamp(28px, 6.8vw, 36px)', fontWeight: 800, letterSpacing: '-0.055em', lineHeight: 1.45, color: 'var(--text)', margin: 0 },
  lead: { fontSize: 12, lineHeight: 1.9, color: 'var(--text-secondary)', marginTop: 14 },
  policy: { paddingBottom: 26 },
  sectionHeading: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 },
  number: { fontSize: 12, fontWeight: 800, color: 'var(--primary-dark)', letterSpacing: '0.04em' },
  sectionTitle: { fontSize: 15, fontWeight: 800, color: 'var(--text)', margin: 0 },
  body: { fontSize: 12, lineHeight: 1.9, color: 'var(--text-secondary)', margin: 0, overflowWrap: 'anywhere' },
  strong: { color: 'var(--text)', fontWeight: 700 },
  details: { marginTop: 12, fontSize: 11, lineHeight: 1.8, color: 'var(--text-secondary)' },
  link: { color: 'var(--text)', textDecoration: 'underline', textUnderlineOffset: 3, textDecorationColor: 'var(--primary)', fontWeight: 700 },
  documentLink: { display: 'inline-flex', alignItems: 'center', gap: 10, fontSize: 12, fontWeight: 700, color: 'var(--text)', textDecoration: 'none', marginTop: 14, minHeight: 32, borderRadius: 4 },
  safety: { background: 'var(--primary-ultra)', borderRadius: 'var(--r-card)', padding: '20px', marginBottom: 30 },
  safetyNote: { marginTop: 10, fontSize: 11, lineHeight: 1.8, color: 'var(--text-secondary)' },
  confirmation: { paddingBottom: 4 },
  confirmHeading: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
  counter: { fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' },
  checkList: { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 22 },
  checkLabel: { display: 'flex', alignItems: 'center', gap: 14, padding: '12px 10px', minHeight: 48, borderRadius: 'var(--r-input)', fontSize: 12, lineHeight: 1.7, color: 'var(--text)', cursor: 'pointer' },
  checkSub: { fontSize: 11, color: 'var(--text-secondary)' },
  checkbox: { width: 22, height: 22, flexShrink: 0, margin: 0, accentColor: 'var(--primary-dark)', cursor: 'pointer' },
  submitBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 18, width: '100%', minHeight: 54, padding: '14px 20px', borderRadius: 'var(--r-btn)', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 800, fontFamily: 'inherit', color: 'var(--surface)', background: 'var(--primary-dark)' },
  submitBtnDisabled: { background: 'var(--bg)', color: 'var(--text-secondary)', cursor: 'not-allowed' },
  footer: { textAlign: 'center', marginTop: 12, fontSize: 10, lineHeight: 1.7, color: 'var(--text-secondary)' },
};

const CSS = `
  .ct-details summary { cursor: pointer; padding: 6px 0; }
  .ct-check { transition: background 150ms ease; }
  .ct-check:focus-within { outline: 2px solid var(--primary); outline-offset: 2px; }
  .ct-submit, .ct-link { transition: transform 180ms cubic-bezier(0.34,1.56,0.64,1), box-shadow 180ms ease; }
  @media (hover: hover) {
    .ct-submit:hover:not(:disabled), .ct-link:hover { transform: translateY(-2px); box-shadow: var(--shadow-sm); }
  }
  .ct-submit:active:not(:disabled) { transform: scale(0.98); }
  @media (prefers-reduced-motion: reduce) {
    .ct-submit, .ct-link, .ct-check { transition: none; }
  }
`;
